// MYG-151497: OAuth device authorization grant (RFC 8628) client for the Tesla Semi QR sign-in.
// Talks to Keycloak directly from the page: /auth/device, /token and /revoke send CORS headers for the origins
// on the client's webOrigins list. PKCE (S256) is required by the realm for the device grant.

import type Api from "../../../../services/api";

const DEVICE_ENDPOINT_PATH = "/protocol/openid-connect/auth/device";
const TOKEN_ENDPOINT_PATH = "/protocol/openid-connect/token";
const REVOKE_ENDPOINT_PATH = "/protocol/openid-connect/revoke";
const DEVICE_CODE_GRANT_TYPE = "urn:ietf:params:oauth:grant-type:device_code";
const DEFAULT_SCOPE = "openid profile email offline_access";
/** The only IAM provider the device grant is wired for; any other server setting is rejected. */
export const IAM_PROVIDER_TYPE_KEYCLOAK = "Keycloak";
const DEFAULT_POLL_INTERVAL_SECONDS = 5;
// RFC 8628 requires expires_in; this only covers a server that leaves it out, so a poll can never run forever.
const DEFAULT_EXPIRES_IN_SECONDS = 600;
// A request on a weak in-vehicle connection can hang for minutes; the code's expiry must not depend on it.
const REQUEST_TIMEOUT_MS = 15 * 1000;
// RFC 8628 section 3.5: on slow_down the client must add 5 seconds to its polling interval.
const SLOW_DOWN_INCREMENT_SECONDS = 5;
const MAX_CONSECUTIVE_NETWORK_FAILURES = 3;
const PKCE_VERIFIER_BYTES = 32;
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1"];

export const DeviceAuthErrorCode = {
    Cancelled: "cancelled",
    Expired: "expired_token",
    Network: "network_error",
    NotConfigured: "iam_not_configured",
    PkceUnavailable: "pkce_unavailable",
    UntrustedVerificationUri: "untrusted_verification_uri",
    Unknown: "unknown_error"
} as const;

export class DeviceAuthError extends Error {
    constructor(readonly code: string, message?: string) {
        super(message || code);
        // Extending Error under an ES5 target breaks instanceof unless the prototype is restored.
        Object.setPrototypeOf(this, DeviceAuthError.prototype);
        this.name = "DeviceAuthError";
    }
}

export interface IDeviceAuthConfig {
    /** The realm URL, e.g. https://login-test.geotab.com/realms/<realm>. Also the token issuer. */
    issuer: string;
    clientId: string;
    scope?: string;
}

export interface IDeviceAuthStart {
    deviceCode: string;
    /** The verification URI with the user code embedded; this is what the QR code encodes. */
    verificationUriComplete: string;
    interval: number;
    expiresIn: number;
    /** PKCE verifier. Stays in the page and is sent only to the token endpoint. */
    codeVerifier: string;
}

// OAuth token response, as returned by Keycloak; the snake_case names are the wire format.
/* eslint-disable @typescript-eslint/naming-convention */
export interface IDeviceAuthTokens {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    token_type: string;
    expires_in: number;
    scope?: string;
}

interface IDeviceAuthorizationResponse {
    device_code?: string;
    user_code?: string;
    verification_uri?: string;
    verification_uri_complete?: string;
    expires_in?: number;
    interval?: number;
    error?: string;
    error_description?: string;
}

interface ITokenResponse extends Partial<IDeviceAuthTokens> {
    error?: string;
    error_description?: string;
}

interface IIamRedirectInformation {
    informationType?: string;
    iamProviderType?: string;
    loginUrl?: string;
    clientId?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IDeviceAuthPoll {
    promise: Promise<IDeviceAuthTokens>;
    cancel: () => void;
}

function isAllowedIssuer(issuer: URL): boolean {
    return issuer.protocol === "https:" || (issuer.protocol === "http:" && LOCAL_HOSTNAMES.indexOf(issuer.hostname) > -1);
}

/**
 * Reads the realm URL and client id that this server uses for IAM. The server is the source of truth for the
 * issuer; only the client id can be overridden, because the Drive client does not allow the device grant yet.
 */
export async function getDeviceAuthConfig(api: Api, server: string, clientIdOverride?: string): Promise<IDeviceAuthConfig> {
    let info: IIamRedirectInformation;
    try {
        info = await api.serverCall(server, "GetIamRedirectInformation", {});
    } catch (error) {
        throw new DeviceAuthError(DeviceAuthErrorCode.Network, `Could not read the IAM settings: ${ error?.message || error }`);
    }

    const clientId = clientIdOverride || info?.clientId;
    if (info?.informationType !== "IAM" || info.iamProviderType !== IAM_PROVIDER_TYPE_KEYCLOAK || !info.loginUrl || !clientId) {
        throw new DeviceAuthError(DeviceAuthErrorCode.NotConfigured, "This server is not set up for Keycloak sign-in.");
    }

    let issuer: URL;
    try {
        issuer = new URL(info.loginUrl);
    } catch {
        throw new DeviceAuthError(DeviceAuthErrorCode.NotConfigured, "The IAM login URL is not valid.");
    }
    if (!isAllowedIssuer(issuer)) {
        throw new DeviceAuthError(DeviceAuthErrorCode.NotConfigured, "The IAM login URL must be https.");
    }

    return { issuer: info.loginUrl.replace(/\/+$/, ""), clientId, scope: DEFAULT_SCOPE };
}

function toBase64Url(bytes: Uint8Array): string {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
    const cryptoApi = window.crypto;
    if (!cryptoApi?.subtle || !cryptoApi.getRandomValues || typeof TextEncoder === "undefined") {
        // crypto.subtle only exists in secure contexts (https or localhost).
        throw new DeviceAuthError(DeviceAuthErrorCode.PkceUnavailable, "This page cannot create a PKCE challenge.");
    }

    const verifier = toBase64Url(cryptoApi.getRandomValues(new Uint8Array(PKCE_VERIFIER_BYTES)));
    const digest = await cryptoApi.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
    return { verifier, challenge: toBase64Url(new Uint8Array(digest)) };
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, onTimeout: () => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
            onTimeout();
            reject(new Error("The request timed out."));
        }, timeoutMs);
        promise.then(
            value => {
                clearTimeout(timer);
                resolve(value);
            },
            error => {
                clearTimeout(timer);
                reject(error);
            }
        );
    });
}

function sendForm(url: string, fields: [string, string][]): { response: Promise<Response>; abort: () => void } {
    const params = new URLSearchParams();
    fields.forEach(([name, value]) => params.append(name, value));

    // AbortController is missing on the oldest browsers we build for (Chrome 53); there the timeout only stops waiting.
    const controller = typeof AbortController === "undefined" ? undefined : new AbortController();

    // A urlencoded POST is a CORS "simple request": no preflight, only the response header is checked.
    const response = fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
        signal: controller?.signal
    });
    return { response, abort: () => controller?.abort() };
}

async function postForm<T>(url: string, fields: [string, string][]): Promise<T> {
    const { response, abort } = sendForm(url, fields);
    const body = await withTimeout(
        response.then(result => {
            // Keycloak answers 4xx with a JSON error body that the caller interprets. A gateway error or a rate limit
            // is not an answer: it is a transient failure, counted like a lost connection.
            if (result.status >= 500 || result.status === 429) {
                throw new Error(`The sign-in service answered ${ result.status }.`);
            }
            return result.json();
        }),
        REQUEST_TIMEOUT_MS,
        abort
    );
    // A proxy or captive portal can answer a literal `null` or a page that parses as something else. That is not an
    // answer from the identity provider: it fails the request like a lost connection would.
    if (!body || typeof body !== "object") {
        throw new Error("The sign-in service sent an answer that could not be read.");
    }
    return body as T;
}

function isSameOrigin(uri: string, issuer: string): boolean {
    try {
        return new URL(uri).origin === new URL(issuer).origin;
    } catch {
        return false;
    }
}

export async function startDeviceAuth(config: IDeviceAuthConfig): Promise<IDeviceAuthStart> {
    const { verifier, challenge } = await createPkcePair();

    let body: IDeviceAuthorizationResponse;
    try {
        body = await postForm<IDeviceAuthorizationResponse>(`${ config.issuer }${ DEVICE_ENDPOINT_PATH }`, [
            ["client_id", config.clientId],
            ["scope", config.scope || DEFAULT_SCOPE],
            ["code_challenge", challenge],
            ["code_challenge_method", "S256"]
        ]);
    } catch (error) {
        throw new DeviceAuthError(DeviceAuthErrorCode.Network, `Could not reach the sign-in service: ${ error?.message || error }`);
    }

    if (body.error || !body.device_code || !body.verification_uri_complete) {
        throw new DeviceAuthError(body.error || DeviceAuthErrorCode.Unknown, body.error_description || body.error);
    }

    // The QR code sends the driver's phone to this address, so it must be on the identity provider we are
    // talking to. Anything else is how a device-code phishing page would look. Only the complete address is used:
    // the QR is the only way to approve a sign-in, so a response without it cannot be completed.
    if (!isSameOrigin(body.verification_uri_complete, config.issuer)) {
        throw new DeviceAuthError(DeviceAuthErrorCode.UntrustedVerificationUri, "The verification address is not on the identity provider.");
    }

    return {
        deviceCode: body.device_code,
        verificationUriComplete: body.verification_uri_complete,
        interval: body.interval || DEFAULT_POLL_INTERVAL_SECONDS,
        expiresIn: body.expires_in || DEFAULT_EXPIRES_IN_SECONDS,
        codeVerifier: verifier
    };
}

/**
 * Best-effort revoke of tokens that will not be used (the driver cancelled, the page moved on, the account is not
 * allowed). The tokens include offline_access, so leaving them would leave a live session at the identity provider.
 * Never throws: there is nothing left to do if it fails.
 */
export async function revokeDeviceAuthTokens(config: IDeviceAuthConfig, tokens: IDeviceAuthTokens): Promise<void> {
    const token = tokens.refresh_token || tokens.access_token;
    if (!token) {
        return;
    }

    try {
        const { response, abort } = sendForm(`${ config.issuer }${ REVOKE_ENDPOINT_PATH }`, [
            ["client_id", config.clientId],
            ["token", token],
            ["token_type_hint", tokens.refresh_token ? "refresh_token" : "access_token"]
        ]);
        await withTimeout(response, REQUEST_TIMEOUT_MS, abort);
    } catch {
        // Best effort only.
    }
}

/**
 * Polls the token endpoint until the driver approves the code on their phone. Self-scheduling rather than
 * setInterval: on slow_down the gap has to widen. The code's expiry runs on its own timer, so a request that hangs
 * cannot keep an expired code on screen. The returned promise rejects with a {@link DeviceAuthError} on expiry,
 * denial, error or cancel().
 */
export function pollDeviceAuth(config: IDeviceAuthConfig, start: IDeviceAuthStart): IDeviceAuthPoll {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    let isSettled = false;
    let rejectPoll: (error: DeviceAuthError) => void = () => undefined;

    const promise = new Promise<IDeviceAuthTokens>((resolve, reject) => {
        let delayMs = Math.max(start.interval, 1) * 1000;
        let consecutiveFailures = 0;

        const settle = (action: () => void) => {
            if (isSettled) {
                return;
            }
            isSettled = true;
            timer !== undefined && clearTimeout(timer);
            expiryTimer !== undefined && clearTimeout(expiryTimer);
            action();
        };

        rejectPoll = (error: DeviceAuthError) => settle(() => reject(error));

        const tick = async () => {
            if (isSettled) {
                return;
            }

            const scheduleNext = () => {
                if (!isSettled) {
                    timer = setTimeout(() => void tick(), delayMs);
                }
            };

            try {
                let body: ITokenResponse;
                try {
                    body = await postForm<ITokenResponse>(`${ config.issuer }${ TOKEN_ENDPOINT_PATH }`, [
                        ["grant_type", DEVICE_CODE_GRANT_TYPE],
                        ["client_id", config.clientId],
                        ["device_code", start.deviceCode],
                        ["code_verifier", start.codeVerifier]
                    ]);
                    consecutiveFailures = 0;
                } catch (error) {
                    consecutiveFailures++;
                    if (consecutiveFailures >= MAX_CONSECUTIVE_NETWORK_FAILURES) {
                        rejectPoll(new DeviceAuthError(DeviceAuthErrorCode.Network, `Lost contact with the sign-in service: ${ error?.message || error }`));
                        return;
                    }
                    scheduleNext();
                    return;
                }

                if (isSettled) {
                    // The driver approved while this request was in flight, but the poll was already cancelled or had
                    // expired, so nobody will receive these tokens. They include offline_access: do not leave them alive.
                    body.access_token && void revokeDeviceAuthTokens(config, body as IDeviceAuthTokens);
                    return;
                }

                if (body.access_token) {
                    const tokens = body as IDeviceAuthTokens;
                    settle(() => resolve(tokens));
                    return;
                }

                if (body.error === "authorization_pending") {
                    scheduleNext();
                    return;
                }

                if (body.error === "slow_down") {
                    delayMs += SLOW_DOWN_INCREMENT_SECONDS * 1000;
                    scheduleNext();
                    return;
                }

                rejectPoll(new DeviceAuthError(body.error || DeviceAuthErrorCode.Unknown, body.error_description || body.error));
            } catch (error) {
                // Nothing may leave tick() unhandled: a poll that neither settles nor reschedules would hang the panel.
                rejectPoll(new DeviceAuthError(DeviceAuthErrorCode.Unknown, `Unexpected sign-in error: ${ error?.message || error }`));
            }
        };

        expiryTimer = setTimeout(() => rejectPoll(new DeviceAuthError(DeviceAuthErrorCode.Expired, "The QR code timed out.")), Math.max(start.expiresIn, 1) * 1000);
        timer = setTimeout(() => void tick(), delayMs);
    });

    return {
        promise,
        cancel: () => rejectPoll(new DeviceAuthError(DeviceAuthErrorCode.Cancelled))
    };
}
